import { DatePicker, TimePicker } from 'antd'
import { Rule } from 'antd/lib/form'
import moment from 'moment-timezone'

import { DateTimeCaptureBlock as DateTimeCaptureBlockType } from '../../types/blueprint'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import { useTemplateRenderer } from '@/context/templateRendererContext'

const DATE_FORMAT = `L`
const TIME_FORMAT = 'hh:mm a'
const FULL_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`
const MINUTE_STEP = 5

type PickerType =
  | 'date'
  | 'time'
  | 'dateTime'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'

type SinglePickerProps = {
  type: PickerType
  value?: moment.Moment
  onChange?: (value: moment.Moment) => void
  readOnlyInput?: boolean
}

const Formats = {
  date: DATE_FORMAT,
  time: TIME_FORMAT,
  dateTime: FULL_FORMAT,
  week: 'wo [week of] YYYY',
  month: 'MMM YYYY',
  quarter: '[Q]Q YYYY',
  year: 'YYYY',
}

export const PickerWithType: React.FC<SinglePickerProps> = ({
  type,
  value,
  onChange,
  readOnlyInput,
}) => {
  const onSelect = (value: moment.Moment) => {
    if (value?.isValid) {
      onChange?.(value)
    }
  }

  const commonProps = {
    value,
    onChange,
    onSelect,
    format: Formats[type],
    minuteStep: MINUTE_STEP,
  }

  switch (type) {
    case 'time':
      return <TimePicker {...commonProps} inputReadOnly={readOnlyInput} />
    case 'date':
      return <DatePicker {...commonProps} inputReadOnly={readOnlyInput} />
    case 'dateTime':
      return (
        <DatePicker
          {...commonProps}
          showTime={{ format: TIME_FORMAT }}
          inputReadOnly={readOnlyInput}
        />
      )
    case 'week':
    case 'month':
    case 'quarter':
    case 'year':
      return (
        <DatePicker
          picker={type}
          {...commonProps}
          inputReadOnly={readOnlyInput}
        />
      )

    default:
      return null
  }
}

export type RangePickerProps = {
  type: PickerType
  value?: [moment.Moment, moment.Moment]
  onChange?: (value: [moment.Moment, moment.Moment]) => void
  readOnlyInput?: boolean
}

export const RangePickerWithType: React.FC<RangePickerProps> = ({
  type,
  value,
  onChange,
  readOnlyInput,
}) => {
  const onCalendarChange = (values: [moment.Moment, moment.Moment]) => {
    if (values?.[0]?.isValid) {
      if (values?.[1]?.isValid) {
        onChange?.(values)
      } else {
        onChange?.([values[0], null])
      }
    } else {
      onChange?.([null, null])
    }
  }

  const commonProps = {
    value,
    onChange,
    onCalendarChange,
    format: Formats[type],
    minuteStep: MINUTE_STEP,
  }

  switch (type) {
    case 'time':
      return (
        <TimePicker.RangePicker
          {...commonProps}
          inputReadOnly={readOnlyInput}
        />
      )
    case 'date':
      return (
        <DatePicker.RangePicker
          {...commonProps}
          inputReadOnly={readOnlyInput}
        />
      )
    case 'dateTime':
      return (
        <DatePicker.RangePicker
          {...commonProps}
          showTime={{ format: TIME_FORMAT }}
          inputReadOnly={readOnlyInput}
        />
      )
    case 'week':
    case 'month':
    case 'quarter':
    case 'year':
      return (
        <DatePicker.RangePicker
          picker={type}
          {...commonProps}
          inputReadOnly={readOnlyInput}
        />
      )

    default:
      return null
  }
}

type Props = {
  block: DateTimeCaptureBlockType
  rules: Rule[]
}

export const DateTimeCaptureBlock: React.FC<Props> = ({ block, rules }) => {
  const { renderOptions } = useTemplateRenderer()

  const { pickerType, captureRange, value } = block

  const initialValue = Array.isArray(value)
    ? value.map((v) => moment(v))
    : moment(value)

  return (
    <CaptureBlockFormItem
      block={block}
      initialValue={value ? initialValue : ''}
      rules={rules}
    >
      {captureRange ? (
        <RangePickerWithType
          type={pickerType ?? 'date'}
          readOnlyInput={renderOptions.readOnlyDateInputs}
        />
      ) : (
        <PickerWithType
          type={pickerType ?? 'date'}
          readOnlyInput={renderOptions.readOnlyDateInputs}
        />
      )}
    </CaptureBlockFormItem>
  )
}
